import * as Rx from 'rxjs';
import { catchError, debounceTime, map, switchMap, tap } from 'rxjs/operators';

import type { ICard } from 'v2/components/Shared/SelectCreditCard';
import type { IPaginationParams, IPaginationResponse } from 'v2/interfaces/pagination';
import type { IShoppingSubscription, SubscriptionProductChangePlan } from 'v2/interfaces/shopping';

import apiService from '../api';
import tableService from '../table';

import type { ApiService } from '../api';
import type { TableService } from '../table';

export class ShoppingSubscriptionService {
  private list$ = new Rx.BehaviorSubject<IShoppingSubscription[]>([]);
  private paginationParams$ = new Rx.BehaviorSubject<IPaginationParams>({ current_page: 1, per_page: 50 });
  private paginationResponse$ = new Rx.BehaviorSubject<IPaginationResponse>(null);
  private current$ = new Rx.BehaviorSubject<IShoppingSubscription>(null);
  private openQuickview$ = new Rx.BehaviorSubject<boolean>(false);

  constructor(private apiService: ApiService, private tableService: TableService) {}

  public getPaginationParams = (): Rx.Observable<IPaginationParams> => this.paginationParams$;

  public getPaginationResponse = (): Rx.Observable<IPaginationResponse> => this.paginationResponse$;

  public setPagination(pagination: IPaginationParams) {
    this.paginationParams$.next(pagination);
  }

  public getPagination = (): Rx.Observable<IPaginationParams> => this.paginationParams$;

  public getCurrent = (): Rx.Observable<IShoppingSubscription> => this.current$;

  public setCurrent = (subscription: IShoppingSubscription): void => this.current$.next(subscription);

  public getQuickview = (): Rx.Observable<boolean> => this.openQuickview$;

  public openQuickview = (subcription: IShoppingSubscription): void => {
    this.setCurrent(subcription);
    this.openQuickview$.next(true);
  };

  public closeQuickview = (): void => {
    this.setCurrent(null);
    this.openQuickview$.next(false);
  };

  public list = (): Rx.Observable<IShoppingSubscription[]> => this.list$;

  public updateSubscriptionInList = (subscription: IShoppingSubscription) => {
    const l = this.list$.value;
    const newList = l.map((sub) => {
      if (sub.id === subscription.id) {
        return subscription;
      }
      return sub;
    });
    this.list$.next(newList);
  };

  public load() {
    return this.getPagination().pipe(
      tap(() => this.tableService.changeLoading(true)),
      debounceTime(10),
      switchMap((pagination) => this.apiService.get('/subscriptions?only_my_subscriptions=true', pagination)),
      tap((response) => this.list$.next(response.data)),
      tap((response) => this.paginationResponse$.next(response.pagination)),
      //tap(response => this.totalItems$.next(response?.pagination?.total_items)),
      tap(() => this.tableService.changeLoading(false)),
      catchError(() => {
        this.tableService.changeLoading(false);
        this.list$.next([]);
        this.paginationResponse$.next(null);
        return Rx.of([]);
      })
    );
  }

  public getSubscription(subscriptionId: number): Rx.Observable<IShoppingSubscription> {
    return this.apiService.get(`/subscriptions/${subscriptionId}`).pipe(map((response) => response.data));
  }

  public canCancel(subscriptionId: number) {
    return this.apiService.get(`/subscriptions/${subscriptionId}/can_customer_cancel`);
  }

  public canRefund(subscriptionId: number, firstInvoiceId: number) {
    return this.apiService.get(`/subscriptions/${subscriptionId}/can_customer_refund`, { first_invoice_id: firstInvoiceId });
  }

  public loadSubscriptionHistory = (productId: number) => {
    return this.apiService.get(`/subscriptions/${productId}/invoices`, { is_customer: true });
  };

  public changeSubscriptionPayment(subscriptionId: number, card: ICard) {
    return this.apiService.post(`/subscriptions/${subscriptionId}/change_payment`, { payment_id: card.id });
  }

  public cancelSubscription(
    subscription: IShoppingSubscription,
    motive: { reason_id: number; reason_description?: string },
    applyRefundByCustomer: boolean = false
  ) {
    return this.apiService.post(`/subscriptions/${subscription.id}/cancel`, {
      ...motive,
      apply_refund_by_customer: applyRefundByCustomer,
      invoice_id: subscription.first_invoice_id,
    });
  }

  public suspendSubscription(subscription: IShoppingSubscription, motive: { reason_id: number; reason_description?: string }) {
    return this.apiService.post(`/subscriptions/${subscription.id}/suspend`, motive);
  }

  public reactivateSubscription(subscription: IShoppingSubscription) {
    return this.apiService.post(`/subscriptions/${subscription.id}/reactivate`, {});
  }

  public getChangePlanProducts(subscriptionId: number): Rx.Observable<SubscriptionProductChangePlan[]> {
    return this.apiService.get(`/subscriptions/${subscriptionId}/change_plan_products`).pipe(map((response) => response.data));
  }

  public changeSubscriptionPlan(subscriptionId: number, productId: number, installments: number) {
    return this.apiService.post(`/subscriptions/${subscriptionId}/change_plan`, {
      product_id: productId,
      installments,
    });
  }

  public producerContact = (invoiceId: string) => {
    return this.apiService.get(`/shopping/${invoiceId}/contactproducer`);
  };
}

const shoppingSubscriptionService = new ShoppingSubscriptionService(apiService, tableService);
export default shoppingSubscriptionService;
